<template>
  <q-tr :props="props" class="odd:bg-neutral-2 !h-48">
    <template v-for="([key, value], index) in tds" :key="value.key">
      <!-- TD ITEM -->
      <q-td v-if="key === 'item'">
        <div
          class="flex items-center gap-2 flex-nowrap size-full"
          :class="{
            '!font-bold': value.hasDescricao || value.totalizador,
            'ml-8': !value.totalizador || value.hasDescricao,
          }"
        >
          <q-icon
            :class="{ 'opacity-0': !value.hasDescricao }"
            name="info"
          />
          <p
            class="text-paragraph-3"
            :class="{ '!font-bold': value.hasDescricao }"
          >
            {{
              props.row.item.chave_traducao_label
                ? t(props.row.item.chave_traducao_label)
                : props.row.item.label
            }}
          </p>
        </div>
        <q-tooltip
          v-if="value.hasDescricao"
          v-bind="tooltipProps"
          anchor="top right"
          self="bottom right"
          class="max-w-[16.25rem] !py-6"
        >
          <div class="flex flex-col">
            <p class="text-headline-3">
              {{
                props.row.item.chave_traducao_label
                  ? t(props.row.item.chave_traducao_label)
                  : props.row.item.label
              }}
            </p>
            <p class="text-paragraph-3 text-white/80">
              {{
                props.row.item.chave_traducao_descricao
                  ? t(props.row.item.chave_traducao_descricao)
                  : props.row.item.descricao
              }}
            </p>
          </div>
        </q-tooltip>
      </q-td>

      <!-- TD Input (MODIFICADO) -->
      <TdInputTemplate
        v-else-if="
          props.cols.find((c) => c.name === key) && !value.totalizador
        "
        :value="value"
        :index="index"
        :tds="tds"
        :value-map
        :validacoes
        :table-structure
        :editing-cell-key="editingCellKey"
        @update:field="onUpdateField"
        @edit-cell="emit('edit-cell', $event)"
        @validacoes="emit('validacoes', $event)"
      />

      <!-- TD TOTALIZADOR -->
      <template
        v-else-if="
          props.cols.find((c) => c.name === key) && value.totalizador
        "
      >
        <q-td
          class="relative overflow-hidden !bg-alert-success-10"
          :class="{
            '!border-t-2 border-solid !border-t-neutral-100':
              value.index === 0 && value.current_month,
          }"
        >
          <span
            class="absolute z-[2] size-full m-auto inset-0"
            :class="{
              '!bg-neutral-100/[0.05]': value.current_month,
            }"
          />
          <p
            class="!font-medium text-alert-success text-center h-[2.25rem] inline-flex justify-center items-center w-full"
          >
            {{ FValue(totalizadores[key].value) }}
          </p>
        </q-td>
      </template>
    </template>
  </q-tr>
</template>

<script setup>
import { computed } from 'vue'
import { tooltipProps } from 'js/utils/GLOBAL'
import { useI18n } from 'vue-i18n'
import TdInputTemplate from 'components/Td/TdInputTemplate.vue'

const { t } = useI18n()

const FValue = (v) => Intl.NumberFormat('pt-BR').format(v)

const { props, rows, editingCellKey } = defineProps({
  props: { type: Object, required: true },
  rows: { type: Array, required: true },
  validacoes: { type: Object, required: false }, // <-- RECEBE COMO PROP
  editingCellKey: { type: String, default: null }, // <-- RECEBE COMO PROP
  tableStructure: { type: Object, required: false },
})

const emit = defineEmits(['update:field', 'edit-cell', 'validacoes']) // <-- DECLARE O NOVO EVENTO

// 1. Converter o objeto row em um array de pares [key, value]
const tds = computed(() => {
  return Object.entries(props.row)
})

// 2. Criar um mapa para encontrar valores rapidamente
const valueMap = computed(() => {
  const map = new Map()
  for (const r of rows) {
    for (const cell of Object.values(r)) {
      if (cell.key) {
        map.set(cell.key, parseFloat(cell.value) || 0)
      }
    }
  }
  return map
})

// 3. Criar uma propriedade computada para todos os totalizadores da linha atual (para exibição)
const totalizadores = computed(() => {
  const totals = {}
  // Itera sobre as células da linha atual
  for (const [key, cellData] of Object.entries(props.row)) {
    // Se for uma célula totalizadora, calcula seu valor
    if (cellData.totalizador && cellData.itemtotalizador_set) {
      const value = cellData.itemtotalizador_set.reduce(
        (acc, item) => {
          // Usa o mapa para pegar o valor atualizado
          const valor = valueMap.value.get(item.campo) || 0

          if (item?.operacao === '+') {
            return acc + valor
          } else if (item?.operacao === '-') {
            return acc - valor
          }
          return acc
        },
        0
      )

      totals[key] = {
        value: value,
        row: { ...cellData },
      }
    }
  }
  return totals
})

// 4. Função para encontrar o totalizador relacionado ao campo atualizado
function getTotalizador(updatedField) {
  const templateId = updatedField.template_id

  // Busca a row que contém o totalizador com esse template_id
  for (const row of rows) {
    for (const [colKey, cellData] of Object.entries(row)) {
      if (
        cellData.totalizador &&
        cellData.template_id === templateId &&
        cellData.itemtotalizador_set
      ) {
        // Calcula o valor do totalizador considerando o novo valor do campo
        const value = cellData.itemtotalizador_set.reduce(
          (acc, item) => {
            let valor
            // Se o campo é o que foi atualizado, usa o novo valor
            if (item.campo === updatedField.key) {
              valor = parseFloat(updatedField.value) || 0
            } else {
              // Senão, usa o valor do valueMap
              valor = valueMap.value.get(item.campo) || 0
            }

            if (item?.operacao === '+') return acc + valor
            if (item?.operacao === '-') return acc - valor
            return acc
          },
          0
        )

        return {
          key: cellData.key,
          value: value,
        }
      }
    }
  }
  return null
}

async function onUpdateField(updatedField) {
  // Encontra o totalizador relacionado (com valor atualizado)
  const itemsToUpdate = [updatedField]
  const totalizador = getTotalizador(updatedField)

  if (totalizador) itemsToUpdate.push(totalizador)

  emit('update:field', itemsToUpdate)
}
</script>

<style lang="scss" scoped></style>
